/**
 * AskAndoCard / AskAndoList — discreet "Chiedi ad Ando" prompts that send a
 * pre-filled question straight to the chat (which is always visible in the left
 * pane) so Ando answers IN PLACE — no navigation to the Mail/Conversazioni tab.
 * Finance-coupled: these are structured-data questions, so they run with "full"
 * focus (RAG + gestionale). Used only by the Finanza hub today.
 *
 * Visual contract — June 2026 unification pass
 * --------------------------------------------
 * Twin of the prompts list rendered inside the inbox detail pane
 * (``InboxDetailPane`` → "ASK ANDO" section). One-line rows with:
 *   - dot accent on the left
 *   - prompt text truncated
 *   - ``ArrowUpRight`` glyph on the right that nudges on hover
 *
 * No bordered card, no inner preview-box, no orange wash. The user
 * recognises this as "the same Ask Ando block they see on every
 * surface", not as a marketing CTA. Pair with :class:`AskAndoSection`
 * below to also get the canonical orange-dot header label.
 *
 * Sends via ``useSendQuery`` — the answer streams into the existing chat.
 */

import { ArrowUpRight } from "lucide-react";
import * as React from "react";
import { Trans } from "@lingui/react/macro";

import { cn } from "@ando/ui";

import { useSendQuery } from "@/features/chat/hooks/useSendQuery";


export interface AskAndoCardProps {
  /** The full prompt — rendered AS the row text (no separate title +
   *  body anymore). Keep ≤ ~90 chars so it fits on one line. */
  readonly prompt: string;
  /** Optional shorter label rendered IN PLACE of the prompt. When
   *  omitted the prompt itself is the row text. */
  readonly title?: string;
  /** @deprecated — kept for back-compat; ignored in the new
   *  inline-row design. */
  readonly description?: string;
  /** @deprecated — kept for back-compat; ignored. */
  readonly cta?: string;
  readonly className?: string;
}


export const AskAndoCard: React.FC<AskAndoCardProps> = ({
  prompt,
  title,
  className,
}) => {
  // Ask the question right here — the chat pane is always visible, so Ando
  // answers in place instead of yanking the user into the Mail/Conversazioni
  // tab. These finance prompts are structured-data questions, so they run with
  // "full" focus (RAG + gestionale).
  const { send } = useSendQuery();
  const handleClick = React.useCallback(() => {
    void send({ text: prompt, configOverride: { focus: "full" } });
  }, [send, prompt]);
  const label = title ?? prompt;
  return (
    <button
      type="button"
      onClick={handleClick}
      title={prompt}
      className={cn(
        "group flex w-full items-center gap-2 rounded-md border border-hairline bg-white/55 px-3 py-2 text-left text-[12px] text-ink-2 transition-colors hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
        className,
      )}
    >
      <span
        aria-hidden
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ando-orange"
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <ArrowUpRight
        className="group-hover:-translate-y-px h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform group-hover:translate-x-px"
        strokeWidth={2}
        aria-hidden
      />
    </button>
  );
};
AskAndoCard.displayName = "AskAndoCard";


export interface AskAndoCardGridProps {
  readonly cards: ReadonlyArray<{
    /** Rendered as the row text when ``title`` is omitted. */
    readonly prompt: string;
    readonly title?: string;
    /** Ignored — kept for back-compat with the old card layout. */
    readonly description?: string;
    readonly cta?: string;
  }>;
  readonly className?: string;
}


/**
 * AskAndoCardGrid — vertical stack of inline AskAndoCard rows.
 *
 * Was a 3-column grid in the old (card-based) design; collapsed to a
 * single column of one-line rows to match the inbox pattern. Use
 * inside a :class:`Panel` after a "Chiedi ad Ando" section label (see
 * :class:`AskAndoSectionLabel`) for the full inbox-style block.
 */
export const AskAndoCardGrid: React.FC<AskAndoCardGridProps> = ({
  cards,
  className,
}) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    {cards.map((c) => (
      <AskAndoCard
        key={c.title ?? c.prompt}
        prompt={c.prompt}
        title={c.title}
      />
    ))}
  </div>
);
AskAndoCardGrid.displayName = "AskAndoCardGrid";


export interface AskAndoSectionLabelProps {
  readonly children?: React.ReactNode;
  readonly className?: string;
}


/**
 * AskAndoSectionLabel — header label that sits ABOVE an
 * :class:`AskAndoCardGrid` to match the inbox pane vocabulary
 * ("CHIEDI AD ANDO" small-caps with a leading orange dot).
 *
 * Reusable so every panel that hosts prompts looks identical.
 */
export const AskAndoSectionLabel: React.FC<AskAndoSectionLabelProps> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      "mb-2 flex items-center gap-1.5 font-semibold text-[10px] text-ando-orange uppercase tracking-[0.08em]",
      className,
    )}
  >
    <span
      aria-hidden
      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ando-orange"
    />
    {children ?? <Trans>Chiedi ad Ando</Trans>}
  </div>
);
AskAndoSectionLabel.displayName = "AskAndoSectionLabel";
